import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/shadcn/accordion";
import { useApiKeyRequestMailto } from "./useApiKeyRequestMailto";

type WebhookGuidanceAccordionProps = {
  defaultOpenSection?: string;
};

export const WebhookGuidanceAccordion = ({ defaultOpenSection }: WebhookGuidanceAccordionProps) => {
  const apiKeyMailto = useApiKeyRequestMailto();
  const payloadExample = `{
  "company": {
    "companyId": "00000000-0000-0000-0000-000000000000"
  },
  "messageId": "00000000-0000-0000-0000-000000000000",
  "eventType": "company.updated",
  "tenantId": "account-number",
  "occurredAtUtc": "2026-02-03T14:43:01.7314694+00:00"
}`;

  const nodeSignatureExample = [
    "const crypto = require('crypto');",
    "",
    "function verifyWebhookSignature(payload, signatureHeader, webhookSecret, toleranceInSeconds = 300) {",
    "  const elements = signatureHeader.split(',');",
    "  const timestamp = elements.find(el => el.startsWith('t=')).split('=')[1];",
    "  const signature = elements.find(el => el.startsWith('v1=')).split('=')[1];",
    "",
    "  const currentTime = Math.floor(Date.now() / 1000);",
    "  if (Math.abs(currentTime - parseInt(timestamp)) > toleranceInSeconds) {",
    "    throw new Error('Webhook timestamp is too old');",
    "  }",
    "",
    "  const signedPayload = `${timestamp}.${payload}`;",
    "  // The secret is base64 encoded, so decode it before using",
    "  const secretBuffer = Buffer.from(webhookSecret, 'base64');",
    "  const expectedSignature = crypto",
    "    .createHmac('sha256', secretBuffer)",
    "    .update(signedPayload, 'utf8')",
    "    .digest('hex');",
    "",
    "  const isValid = crypto.timingSafeEqual(",
    "    Buffer.from(signature, 'hex'),",
    "    Buffer.from(expectedSignature, 'hex')",
    "  );",
    "",
    "  if (!isValid) throw new Error('Invalid webhook signature');",
    "  return true;",
    "}"
  ].join("\n");

  const csharpSignatureExample = [
    "using System.Security.Cryptography;",
    "using System.Text;",
    "",
    "bool VerifyWebhookSignature(string payload, string signatureHeader, string webhookSecret, int toleranceInSeconds = 300)",
    "{",
    "    var elements = signatureHeader.Split(',');",
    "    var timestamp = elements.First(e => e.StartsWith(\"t=\")).Substring(2);",
    "    var signature = elements.First(e => e.StartsWith(\"v1=\")).Substring(3);",
    "",
    "    var currentTime = DateTimeOffset.UtcNow.ToUnixTimeSeconds();",
    "    var webhookTime = long.Parse(timestamp);",
    "    ",
    "    if (Math.Abs(currentTime - webhookTime) > toleranceInSeconds)",
    "        throw new Exception(\"Webhook timestamp is too old\");",
    "",
    "    var signedPayload = $\"{timestamp}.{payload}\";",
    "    var secretBytes = Convert.FromBase64String(webhookSecret);",
    "    ",
    "    using var hmac = new HMACSHA256(secretBytes);",
    "    var computedHash = hmac.ComputeHash(Encoding.UTF8.GetBytes(signedPayload));",
    "    var expectedSignature = Convert.ToHexString(computedHash).ToLowerInvariant();",
    "",
    "    return CryptographicOperations.FixedTimeEquals(",
    "        Convert.FromHexString(signature),",
    "        Convert.FromHexString(expectedSignature)",
    "    );",
    "}"
  ].join("\n");

  return (
    <>
      <div className="md:col-span-5 mt-4">
        <div className="text-gray-600">
          <p className="font-semibold text-lg">General and Technical Guidance</p>
          <hr />
        </div>
      </div>
      <div className="md:col-span-5">
        <Accordion
          type="single"
          collapsible
          defaultValue={defaultOpenSection || ""}
          className="w-full">
          <AccordionItem value="General information">
            <AccordionTrigger className="text-kPurple-700 text-base font-semibold">What is a webhook and what is it used for?</AccordionTrigger>
            <AccordionContent>
              A webhook is a mechanism that allows one system to send real-time data to another system via HTTP callbacks. When a specific event occurs in the source system, a POST request containing event data (the "payload") is sent to a pre-configured URL endpoint in your application.
              <br /><br />
              Webhooks are commonly used to notify external systems about changes or events, such as updates to records, status changes, or other business events. By consuming webhook payloads, your application can automate workflows, synchronize data, or trigger custom logic in response to these events.
              <br /><br />
              To use a webhook, you must provide a publicly accessible endpoint that can receive and process HTTP POST requests with JSON payloads as described in the example below.
              <br /><br />
              <b>Note:</b> You can use webhooks on their own, without calling the <a href="https://api.vecta.net" target="_blank" rel="noreferrer"><b>Vecta API</b></a>. If you want more detail 
              about the record linked to an event, you can use the ID in the payload with the <a href="https://api.vecta.net" target="_blank" rel="noreferrer"><b>Vecta API</b></a> to 
              retrieve additional information. This gives you the most complete experience, but it is optional.
              <br /><br />
              To obtain a <b>API key</b>, please contact your <a href={apiKeyMailto} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">account manager</a>.
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="payload">
            <AccordionTrigger className="text-kPurple-700 text-base font-semibold">Webhook Example payload.</AccordionTrigger>
            <AccordionContent>
              Webhook payloads are delivered as JSON objects with the following structure:
              <br /><br />
              This is an example of a <code>customer.updated</code> event payload. See Above for more events.
              <pre className="bg-gray-100 rounded p-3 mt-3 text-xs overflow-x-auto">
              {payloadExample}
              </pre>
              <ul className="mt-3 list-disc list-inside text-sm text-gray-700">
                <li>
                  <strong>company.companyId</strong>: The unique identifier for the company related to the event.
                  This companyId can be used in combination with the <a href="https://api.vecta.net" target="_blank" rel="noreferrer"><b>Vecta API</b></a> to retrieve more information about the company.
                  Other events e.g. opportunity, activity, project will have equivalent objects with their respective IDs,
                  e.g. <code>opportunity.opportunityId</code>, <code>activity.activityId</code>, <code>project.projectId</code>.
                </li>
                <li>
                  <strong>messageId</strong>: A unique identifier for this webhook message. Useful for idempotency and tracking.
                </li>
                <li>
                  <strong>eventType</strong>: The type of event that triggered the webhook (e.g., <code>company.updated</code>).
                </li>
                <li>
                  <strong>tenantId</strong>: Your customer account number, used when logging into Vecta.
                </li>
                <li>
                  <strong>occurredAtUtc</strong>: The UTC timestamp when the event occurred.
                </li>
              </ul>
              <p className="mt-3 text-sm text-gray-600">
                Your endpoint should expect this structure and validate the <code>eventType</code> to handle events appropriately.
              </p>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="security">
            <AccordionTrigger className="text-kPurple-700 text-base font-semibold">How can I validate the payload came from a trusted source?</AccordionTrigger>
            <AccordionContent>
              Webhook payloads are secured using HMAC-SHA256 signatures. Each request includes an <code>X-Signature</code> header containing a timestamp and a signature.
              <br /><br />
              To verify authenticity:
              <ol className="mt-2 list-decimal list-inside text-sm text-gray-700">
                <li>
                  Extract the <code>X-Signature</code> header from the request. It will look like: <br />
                  <code>t=1640995200,v1=abcdef123456...</code>
                </li>
                <li>
                  Validate the timestamp (<code>t</code>) to prevent replay attacks. Reject requests older than 5 minutes.
                </li>
                <li>
                  Build the signed payload: <code>{`{timestamp}.{raw_body}`}</code> (concatenate the timestamp and the exact request body).
                </li>
                <li>
                  Compute the HMAC-SHA256 signature using your webhook secret and the signed payload.
                </li>
                <li>
                  Compare the computed signature to the <code>v1</code> value from the header using a timing-safe comparison.
                </li>
              </ol>
              <p className="mt-3 text-sm text-gray-600">
                If the signatures match and the timestamp is valid, the payload is authentic.
              </p>
              <p className="mt-3 font-semibold">Example: Node.js</p>
              <ul className="list-disc list-inside text-sm text-gray-700 mt-2">
                <li>Ensure you're using the appropriate namespaces for cryptographic operations.</li>
                <li>Ensure you're using the raw payload body as JSON viewers can change the formatting which will invalidate the signature.</li>
                <li>Ensure you use the secret as provided in the webhook configuration; it is base64 encoded.</li>
              </ul>
              <pre className="bg-gray-100 rounded p-3 mt-2 text-xs overflow-x-auto">
{nodeSignatureExample}
              </pre>
              <p className="mt-3 font-semibold">Example: C# (.NET)</p>
              <ul className="list-disc list-inside text-sm text-gray-700 mt-2">
                <li>Ensure you're using the appropriate namespaces for cryptographic operations.</li>
                <li>Ensure you're using the raw payload body as JSON viewers can change the formatting which will invalidate the signature.</li>
                <li>Ensure you use the secret as provided in the webhook configuration; it is base64 encoded.</li>
              </ul>
              <pre className="bg-gray-100 rounded p-3 mt-2 text-xs overflow-x-auto">
        {csharpSignatureExample}
              </pre>
              <p className="mt-3 text-sm text-gray-600">
                Although not essential you should always attempt to verify the signature and timestamp before processing the payload.
              </p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </>
  );
};